import { createSelector } from '@ngrx/store';
import { getCurrentStudies } from './study.selectors';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { IEntityMap } from '@core/state/entities/entity';
import {
  AnnotationStatus,
  annotationStatus,
  annotationStatuses,
  IStudyDetails,
} from '../study-details/study-details.entity';
import { ISearch } from '../search/search.entity';
import { IStudy } from '@core/state/entities/study/study.entity';
import {
  selectCurrentStage,
  selectStagesForCurrentProject,
} from '../stage/stage.selectors';
import { contains, has, IStage } from '../stage/stage.entity';
import * as _ from 'lodash-es';
import { ISession } from '@app/core/state/entities/session/session.entity';
import { selectCurrentUserId } from '@app/core/auth/auth.selectors';
import {
  AnnotationSessionProgress,
  IStageInfo,
} from '@app/core/state/entities/study/project-study.viewmodel';
import { IProjectDetails } from '@app/core/state/entities/project-details/project-details.entity';
import { selectCurrentProjectDetails } from '@app/core/state/entities/project-details/project-details.selectors';
import {
  SessionStatus,
  UserInclusionFilter,
} from '@core/services/api-client.generated';

export interface IStageStudyVM {
  id: string;
  authors: string[];
  systematicSearchId: string;
  publicationName: {
    name: string;
    alternateName: string;
  };
  sessions: string[];
  screening: string;
  studyDetails: string;
  numberOfScreenings: number;
  journal: string;
  studyId: string;
  title: string;
  abstract: string;
  projectId: string;
  referenceType: string;
  linkToPdf: string;
  url: string;
  firstAuthor: string;
  systematicSearchName: string;
  annotationStatus: AnnotationStatus;
  outcomeDataStatus: string;
  screeningDecision: screeningDecisionText;
  year: number;
  inclusionStatus: string;

  stageInfo: IStageInfo[];
  random: string | null;
  blind: string | null;
  interest: string | null;
  welfare: string | null;
  exclusion: string | null;
}

export type screeningDecisionText =
  | 'Included'
  | 'Excluded'
  | 'Not Screened'
  | 'Unavailable';

export const selectCurrentStageStudyVms = createSelector(
  getCurrentStudies,
  selectCurrentUserId,
  selectCurrentStage,
  selectStagesForCurrentProject,
  selectEntityMap('studyDetails'),
  selectEntityMap('searches'),
  selectEntityMap('sessions'),
  selectCurrentProjectDetails,
  (
    currentStudies: IStudy[],
    currentUserId: string,
    stage: IStage | null,
    stages: IStage[],
    studyDetailsMap: IEntityMap<IStudyDetails>,
    searchMap: IEntityMap<ISearch>,
    sessionsMap: IEntityMap<ISession>,
    projectDetails: IProjectDetails
  ): IStageStudyVM[] =>
    currentStudies.map((study: IStudy): IStageStudyVM => {
      if (!study) {
        const cs = currentStudies;
        debugger;
      }

      const studySessions = study.sessions
        .map((sid) => sessionsMap[sid])
        .filter((s) => !!s);

      const stageInfo: IStageInfo[] = stages.map((st) => {
        const userAnnotationSession =
          studySessions.filter(
            (s) =>
              s.investigatorId === currentUserId &&
              s.reconciliation === false &&
              s.stageId === st.id
          )[0] || null;
        const annotationSessions = studySessions.filter(
          (s) => s.stageId === st.id && s.reconciliation === false
        );
        const userAnStatus: AnnotationSessionProgress = !userAnnotationSession
          ? 'None'
          : userAnnotationSession.status === SessionStatus.Incomplete
            ? 'In Progress'
            : 'Completed';
        return {
          stageId: st.id,
          userAnnotationSessionStatus: userAnStatus,
          numberOfIncompleteAnnotationSessions: annotationSessions.filter(
            (s) => s.status === SessionStatus.Incomplete
          ).length,
          numberOfCompletedAnnotationSessions: annotationSessions.filter(
            (s) => s.status === SessionStatus.Completed
          ).length,
          numberOfIncompleteSessionsWithOutcomeData: annotationSessions.filter(
            (s) =>
              s.status === SessionStatus.Incomplete && s.outcomeData.length > 0
          ).length,
          numberOfCompletedSessionsWithOutcomeData: annotationSessions.filter(
            (s) =>
              s.status === SessionStatus.Completed && s.outcomeData.length > 0
          ).length,
          userOutcomeData: userAnnotationSession
            ? userAnnotationSession.outcomeData.length > 0
            : false,
          annotationsComplete:
            st.sessionCountTarget <= annotationSessions.length,
          screeningActive: contains(st.reviewMode, has.screening),
          annotationActive: contains(st.reviewMode, has.annotation),
        };
      });

      const currentUserCandidateSession = studySessions.find(
        (sess) =>
          sess.investigatorId === currentUserId &&
          !sess.reconciliation &&
          sess.stageId === stage?.id
      );
      const allCandidateSessions = studySessions.filter(
        (sess) => !sess.reconciliation && sess.stageId === stage?.id
      );
      let allSlotsFilled = false;
      if (stage?.sessionCountTarget) {
        allSlotsFilled =
          allCandidateSessions.length >= stage?.sessionCountTarget;
      }
      const studyDetails = studyDetailsMap[study.id];
      const robV2 = studyDetails?.riskOfBiasInfo?.result;
      return {
        ...study,
        annotationStatus:
          annotationStatuses[
            currentUserCandidateSession
              ? currentUserCandidateSession.status === SessionStatus.Completed
                ? annotationStatus.Completed
                : annotationStatus.InProgress
              : allSlotsFilled
                ? annotationStatus.NotAvailable
                : annotationStatus.NotStarted
          ],
        screeningDecision: <screeningDecisionText>(
          _.startCase(UserInclusionFilter[studyDetails?.investigatorDecision])
        ),
        outcomeDataStatus:
          (currentUserCandidateSession?.outcomeData?.length ?? 0 > 0)
            ? 'Outcome Data'
            : 'None Yet',
        stageInfo,
        journal: study.publicationName.alternateName,
        systematicSearchName: searchMap[study.systematicSearchId]?.name,
        firstAuthor: study.authors[0] || '',
        inclusionStatus: studyDetails?.inclusionStatus,
        random: toPercent(robV2?.random),
        blind: toPercent(robV2?.blind),
        interest: toPercent(robV2?.interest),
        welfare: toPercent(robV2?.welfare),
        exclusion: toPercent(robV2?.exclusion),
      };
    })
);

export const toPercent = (value: number | undefined): string | null => {
  return value ? `${Math.round(value * 1000) / 10}%` : null;
};
